import { Dialog, DialogPanel, Transition } from '@headlessui/react';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import DrawerWithForm from './Drawer';

const Gallery = ({ images, uptitle }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const dragging = useRef(false);
  const [offset, setOffset] = useState(0);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const threshold = 50;

  const hasImages = images && images.length > 0;

  const formattedImages = hasImages
    ? images.map((img, index) => {
        const gatsbyImage = img.localFile?.childImageSharp ? getImage(img.localFile) : null;

        const highResUrl = img.url;
        const previewUrl = img.formats?.medium?.url || img.url;

        return {
          gatsbyImage,
          highResUrl,
          previewUrl,
          alt:
            img.alternativeText ||
            `Imagen ${index + 1} de ${images.length} de ejemplo de ${uptitle}`,
        };
      })
    : [];

  const needsPlaceholder = formattedImages.length % 2 !== 0;

  const openLightbox = (index) => setSelectedIndex(index);

  const closeLightbox = useCallback(() => {
    setSelectedIndex(null);
    setOffset(0);
  }, []);

  const prevImage = useCallback(() => {
    setOffset(-100);
    setTimeout(() => {
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : formattedImages.length - 1));
      setOffset(0);
    }, 200);
  }, [formattedImages.length]);

  const nextImage = useCallback(() => {
    setOffset(100);
    setTimeout(() => {
      setSelectedIndex((prev) => (prev < formattedImages.length - 1 ? prev + 1 : 0));
      setOffset(0);
    }, 200);
  }, [formattedImages.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    dragging.current = true;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
    setOffset(touchEndX.current - touchStartX.current);
  };

  const handleTouchEnd = () => {
    dragging.current = false;

    const swipeDistance = touchStartX.current - touchEndX.current;
    if (swipeDistance > threshold) {
      nextImage();
    } else if (swipeDistance < -threshold) {
      prevImage();
    } else {
      setOffset(0);
    }
  };

  useEffect(() => {
    if (selectedIndex !== null) {
      const handleKeyDown = (e) => {
        if (e.key === 'ArrowLeft') {
          prevImage();
        } else if (e.key === 'ArrowRight') {
          nextImage();
        } else if (e.key === 'Escape') {
          closeLightbox();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedIndex, prevImage, nextImage, closeLightbox]);

  return (
    <>
      {hasImages && (
        <>
          <div className="grid grid-cols-2 gap-6">
            {formattedImages.map((img, index) => (
              <div
                key={index}
                role="button"
                tabIndex={0}
                aria-label={`Abrir imagen ${index + 1} de ${formattedImages.length}`}
                className="cursor-pointer overflow-hidden rounded-lg hover:scale-105 transition-transform"
                onClick={() => openLightbox(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    openLightbox(index);
                  }
                }}
              >
                {img.gatsbyImage ? (
                  <GatsbyImage
                    image={img.gatsbyImage}
                    alt={img.alt}
                    className="w-full h-auto object-cover rounded-lg"
                  />
                ) : (
                  <img src={img.previewUrl} alt={img.alt} className="w-full h-auto rounded-lg" />
                )}
              </div>
            ))}

            {/* Placeholder si el número es impar */}
            {needsPlaceholder && (
              <DrawerWithForm
                buttonClasses="rounded-lg bg-transparent hover:bg-slate-100 hover:text-slate-700 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-3xl font-bold"
                text="+"
              />
            )}
          </div>

          <Transition show={selectedIndex !== null} as={React.Fragment}>
            <Dialog
              as="div"
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 md:bg-black/80"
              onClose={closeLightbox}
            >
              <div className="relative max-w-5xl w-full px-4">
                <DialogPanel>
                  <div
                    className="flex justify-center items-center transition-transform duration-200 ease-in-out"
                    style={{
                      transform: `translateX(${offset}px)`,
                    }}
                  >
                    <img
                      src={formattedImages[selectedIndex]?.highResUrl}
                      alt={formattedImages[selectedIndex]?.alt}
                      className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                    />
                  </div>

                  <button className="fixed top-4 right-4 text-white" onClick={closeLightbox}>
                    <XMarkIcon className="w-8 h-8" />
                  </button>
                  <button
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white"
                    onClick={prevImage}
                  >
                    <ChevronLeftIcon className="w-4 h-4 md:w-10 md:h-10" />
                  </button>
                  <button
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white"
                    onClick={nextImage}
                  >
                    <ChevronRightIcon className="w-4 h-4 md:w-10 md:h-10" />
                  </button>
                </DialogPanel>
              </div>
            </Dialog>
          </Transition>
        </>
      )}
    </>
  );
};

export default Gallery;
